import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';

const AnimatedNumber = ({ 
  value, 
  duration = 1, 
  className = "", 
  prefix = "", 
  suffix = "",
  decimals = 2,
  formatNumber = true,
  forceAnimation = false
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const isFirstRender = useRef(true);
  const lastValue = useRef(0);

  useEffect(() => {
    if (value === lastValue.current && !forceAnimation && !isFirstRender.current) {
      return;
    }

    lastValue.current = value;
    
    if (isFirstRender.current) {
      if (value === 0) {
        setDisplayValue(0);
      } else {
        setDisplayValue(0);
        setTimeout(() => {
          animateToValue(0, value);
        }, 100);
      }
      isFirstRender.current = false;
      return;
    }

    animateToValue(displayValue, value);
  }, [value, forceAnimation]);

  const animateToValue = (startValue, endValue) => {
    if (startValue === endValue) return;
    
    setIsAnimating(true);
    const difference = endValue - startValue;
    const startTime = Date.now();
    
    const animate = () => {
      const now = Date.now();
      const elapsed = (now - startTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = startValue + (difference * easeOutQuart);
      
      setDisplayValue(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(endValue);
        setIsAnimating(false);
      }
    };
    
    requestAnimationFrame(animate);
  };

  const formatDisplayValue = (val) => {
    if (formatNumber) {
      return val.toFixed(decimals);
    }
    return Math.round(val).toLocaleString();
  };

  return (
    <motion.span 
      className={className}
      initial={{ scale: 0.95, opacity: 0.8 }}
      animate={{ 
        scale: isAnimating ? 1.02 : 1,
        opacity: 1
      }}
      transition={{ duration: 0.2 }}
    >
      {prefix}{formatDisplayValue(displayValue)}{suffix}
    </motion.span>
  );
};

// 数字翻滚组件 - 类似老虎机效果
const RollingNumber = ({ 
  value, 
  className = "", 
  prefix = "", 
  suffix = "",
  digitHeight = 40 
}) => {
  const digits = value.toString().split('');
  
  return (
    <span className={`inline-flex items-center ${className}`}>
      {prefix}
      <div className="flex">
        {digits.map((digit, index) => (
          <div 
            key={index}
            className="relative overflow-hidden inline-block"
            style={{ height: digitHeight }}
          >
            <motion.div
              key={digit}
              initial={{ y: digitHeight }}
              animate={{ y: 0 }}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: [0.25, 0.1, 0.25, 1]
              }}
              className="absolute inset-0 flex items-center justify-center"
            >
              {digit}
            </motion.div>
          </div>
        ))}
      </div>
      {suffix}
    </span>
  );
};

export { AnimatedNumber, RollingNumber }; 